import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import AuthService from "../service/AmplifyAuth";
import { Button, Field } from "../components/UI";
import { apiHeaders, useToast } from "../hooks";

const TICKET_STATUS_LABELS = {
  open: "Open",
  pending: "Pending",
  solved: "Solved",
};

const formatTicketDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

const getThreadSenderLabel = (entry) => {
  if (entry?.author === "host") {
    return "You";
  }
  return "Trustd";
};

export default function Contact() {
  const cachedUser = AuthService.getCachedUserAttributes?.() || null;
  const [user, setUser] = useState(cachedUser);
  const [contactForm, setContactForm] = useState({ subject: "", message: "" });
  const [contactErrors, setContactErrors] = useState({});
  const [replyError, setReplyError] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [tickets, setTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [isLoadingTickets, setIsLoadingTickets] = useState(true);
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  const [isSendingReply, setIsSendingReply] = useState(false);
  const addToast = useToast();

  const selectedTicket = useMemo(
    () =>
      tickets.find((ticket) => ticket.id === selectedTicketId) ||
      tickets[0] ||
      null,
    [tickets, selectedTicketId],
  );

  const ticketSummary = useMemo(() => {
    const open = tickets.filter((ticket) => ticket.status === "open").length;
    const pending = tickets.filter(
      (ticket) => ticket.status === "pending",
    ).length;
    const solved = tickets.filter(
      (ticket) => ticket.status === "solved",
    ).length;

    return {
      total: tickets.length,
      open,
      pending,
      solved,
    };
  }, [tickets]);

  const getAuthorizedHeaders = useCallback(async () => {
    const session = await AuthService.currentSession();
    const accessToken = session?.accessToken;
    if (!accessToken) {
      throw new Error("Missing host session.");
    }
    return {
      ...apiHeaders,
      Authorization: `Bearer ${accessToken}`,
    };
  }, []);

  const refreshTickets = useCallback(
    async ({ preferredTicketId = null, showLoading = false } = {}) => {
      if (showLoading) {
        setIsLoadingTickets(true);
      }
      try {
        const headers = await getAuthorizedHeaders();
        const response = await axios.get("/v1/tickets", {
          headers,
          timeout: 0,
        });
        const nextTickets = Array.isArray(response?.data?.tickets)
          ? response.data.tickets
          : [];
        setTickets(nextTickets);
        setSelectedTicketId((current) => {
          const preferred = preferredTicketId || current;
          if (
            preferred &&
            nextTickets.some((ticket) => ticket.id === preferred)
          ) {
            return preferred;
          }
          return nextTickets[0]?.id || null;
        });
      } catch (error) {
        addToast({
          severity: "error",
          summary: "Tickets Unavailable",
          detail: getErrorMessage(
            error,
            "Unable to load your support tickets.",
          ),
          life: 5000,
        });
      } finally {
        if (showLoading) {
          setIsLoadingTickets(false);
        }
      }
    },
    [addToast, getAuthorizedHeaders],
  );

  const upsertTicket = useCallback((nextTicket) => {
    if (!nextTicket?.id) return;
    setTickets((current) => {
      const rest = current.filter((ticket) => ticket.id !== nextTicket.id);
      return [nextTicket, ...rest];
    });
    setSelectedTicketId(nextTicket.id);
  }, []);

  const updateContactField = (field, value) => {
    setContactForm((form) => ({ ...form, [field]: value }));
    setContactErrors((errors) => {
      if (!errors[field] || !value.trim()) return errors;
      const nextErrors = { ...errors };
      delete nextErrors[field];
      return nextErrors;
    });
  };

  const updateReplyMessage = (value) => {
    setReplyMessage(value);
    if (value.trim()) setReplyError("");
  };

  useEffect(() => {
    let isMounted = true;

    async function load() {
      try {
        const authenticated =
          await AuthService.currentAuthenticatedUserAttributes();
        if (!isMounted) return;
        setUser(authenticated);
        await refreshTickets({ showLoading: true });
      } catch (error) {
        if (!isMounted) return;
        setIsLoadingTickets(false);
        addToast({
          severity: "error",
          summary: "Session Error",
          detail: getErrorMessage(
            error,
            "Please sign in again to access support tickets.",
          ),
          life: 5000,
        });
      }
    }

    load();
    return () => {
      isMounted = false;
    };
  }, [addToast, refreshTickets]);

  useEffect(() => {
    if (!user) return undefined;

    const intervalId = window.setInterval(() => {
      void refreshTickets({ preferredTicketId: selectedTicket?.id || null });
    }, 10000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [refreshTickets, selectedTicket?.id, user]);

  const send = async () => {
    const subject = contactForm.subject.trim();
    const message = contactForm.message.trim();

    if (!subject || !message) {
      setContactErrors({
        subject: subject ? "" : "Subject is required",
        message: message ? "" : "Message is required",
      });
      addToast({
        severity: "warn",
        summary: "Incomplete Form",
        detail: "Please fill in all required fields.",
        life: 5000,
      });
      return;
    }

    setIsCreatingTicket(true);
    try {
      const headers = await getAuthorizedHeaders();
      const response = await axios.post(
        "/v1/tickets",
        { subject, message },
        { headers, timeout: 0 },
      );

      const createdTicket = response?.data?.ticket || null;
      upsertTicket(createdTicket);
      setContactForm({ subject: "", message: "" });
      addToast({
        severity: "success",
        summary: "Ticket Opened",
        detail: createdTicket?.id
          ? `Support ticket ${createdTicket.id} opened.`
          : "Your support ticket is opened.",
        life: 5000,
      });
    } catch (error) {
      addToast({
        severity: "error",
        summary: "Message Failed",
        detail: getErrorMessage(
          error,
          "An error occurred while sending your message. Please try again.",
        ),
        life: 5000,
      });
    } finally {
      setIsCreatingTicket(false);
    }
  };

  const sendReply = async () => {
    const message = replyMessage.trim();
    if (!selectedTicket?.id) return;

    if (!message) {
      setReplyError("Reply is required");
      addToast({
        severity: "warn",
        summary: "Reply Required",
        detail: "Please enter a message before sending.",
        life: 5000,
      });
      return;
    }

    setIsSendingReply(true);
    try {
      const headers = await getAuthorizedHeaders();
      const response = await axios.post(
        `/v1/tickets/${selectedTicket.id}/messages`,
        { message },
        { headers, timeout: 0 },
      );
      const updatedTicket = response?.data?.ticket || null;
      upsertTicket(updatedTicket);
      setReplyMessage("");
    } catch (error) {
      addToast({
        severity: "error",
        summary: "Reply Failed",
        detail: getErrorMessage(error, "Unable to send your reply."),
        life: 5000,
      });
    } finally {
      setIsSendingReply(false);
    }
  };

  if (!user) return <div className="loading-container" />;

  return (
    <div className="contact-page-modern">
      <div className="contact-modern">
        <div className="contact-page-head">
          <div>
            <p className="dashboard-section-kicker">Support Center</p>
            <h2>Talk to the Admin Team</h2>
            <p className="dashboard-section-note">
              Open a ticket, track status, and continue the conversation in one
              place.
            </p>
          </div>
          <div className="contact-summary">
            <div>
              <strong>{ticketSummary.total}</strong>
              <span>Total</span>
            </div>
            <div>
              <strong>{ticketSummary.open}</strong>
              <span>Open</span>
            </div>
            <div>
              <strong>{ticketSummary.pending}</strong>
              <span>Pending</span>
            </div>
            <div>
              <strong>{ticketSummary.solved}</strong>
              <span>Solved</span>
            </div>
          </div>
        </div>

        <div className="support-ticket-layout">
          <div className="support-ticket-left-column">
            <section className="support-ticket-compose">
              <div className="support-card-head">
                <h3>Open New Ticket</h3>
              </div>
              <div className="support-card-body">
                <Field label="Subject" required error={contactErrors.subject}>
                  <input
                    className="form-control"
                    placeholder="Short summary of your request"
                    value={contactForm.subject}
                    onChange={(event) =>
                      updateContactField("subject", event.target.value)
                    }
                  />
                </Field>
                <Field label="Message" required error={contactErrors.message}>
                  <textarea
                    className="form-control"
                    placeholder="Describe your issue with as much detail as possible"
                    rows="4"
                    value={contactForm.message}
                    onChange={(event) =>
                      updateContactField("message", event.target.value)
                    }
                  />
                </Field>
                <Button
                  label={isCreatingTicket ? "Opening..." : "Open Ticket"}
                  disabled={isCreatingTicket}
                  onClick={send}
                />
              </div>
            </section>

            <section className="support-ticket-list">
              <div className="support-ticket-list-head">
                <h3>My Tickets</h3>
                <span>{ticketSummary.total} total</span>
              </div>
              <div className="support-ticket-list-body">
                {isLoadingTickets ? (
                  <div className="loading-container" />
                ) : tickets.length === 0 ? (
                  <div className="support-empty-state">
                    <strong>No tickets yet</strong>
                    <p className="mb-0">
                      Open your first ticket to start a conversation with
                      support.
                    </p>
                  </div>
                ) : (
                  tickets.map((ticket) => (
                    <button
                      key={ticket.id}
                      type="button"
                      className={`support-ticket-item ${
                        ticket.id === selectedTicket?.id ? "is-active" : ""
                      }`}
                      onClick={() => setSelectedTicketId(ticket.id)}
                    >
                      <div className="support-ticket-item-head">
                        <strong>{ticket.subject}</strong>
                        <span
                          className={`status-chip status-${ticket.status || "open"}`}
                        >
                          {TICKET_STATUS_LABELS[ticket.status] || "Open"}
                        </span>
                      </div>
                      <small>{ticket.id}</small>
                      <small>
                        Updated {formatTicketDate(ticket.updatedAt)}
                      </small>
                    </button>
                  ))
                )}
              </div>
            </section>
          </div>

          <section className="support-ticket-thread">
            {selectedTicket ? (
              <>
                <div className="support-ticket-thread-head">
                  <div>
                    <h3>{selectedTicket.subject}</h3>
                    <small>
                      {selectedTicket.id} · Updated{" "}
                      {formatTicketDate(selectedTicket.updatedAt)}
                    </small>
                  </div>
                  <span
                    className={`status-chip status-${selectedTicket.status || "open"}`}
                  >
                    {TICKET_STATUS_LABELS[selectedTicket.status] || "Open"}
                  </span>
                </div>

                <div className="support-ticket-messages">
                  {(selectedTicket.messages || []).length === 0 ? (
                    <div className="support-empty-state">
                      <strong>No messages yet</strong>
                      <p className="mb-0">
                        Write the first message to this ticket.
                      </p>
                    </div>
                  ) : (
                    (selectedTicket.messages || []).map((entry) => (
                      <div
                        key={entry.id}
                        className={`support-ticket-message ${
                          entry.author === "host" ? "is-host" : "is-support"
                        }`}
                      >
                        <div className="support-ticket-message-meta">
                          <strong>{getThreadSenderLabel(entry)}</strong>
                          <span>{formatTicketDate(entry.createdAt)}</span>
                        </div>
                        <p className="mb-0">{entry.message}</p>
                      </div>
                    ))
                  )}
                </div>

                <div className="support-reply-bar">
                  <Field label="Reply" required error={replyError}>
                    <textarea
                      className="form-control"
                      rows="1"
                      placeholder="Write your reply to support..."
                      value={replyMessage}
                      onChange={(event) =>
                        updateReplyMessage(event.target.value)
                      }
                      disabled={selectedTicket.status === "solved"}
                    />
                  </Field>
                  <Button
                    label={isSendingReply ? "Sending..." : "Send Reply"}
                    disabled={
                      isSendingReply || selectedTicket.status === "solved"
                    }
                    onClick={sendReply}
                  />
                </div>
                {selectedTicket.status === "solved" ? (
                  <div className="support-ticket-closed-note">
                    This ticket is solved. Open a new ticket for additional
                    requests.
                  </div>
                ) : null}
              </>
            ) : (
              <div className="support-empty-state">
                <strong>Select a ticket</strong>
                <p className="mb-0">
                  Pick a ticket from the list to view and continue the
                  conversation.
                </p>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
